import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useLocation } from "react-router-dom";
import { useSignatureStateManager } from "./hooks/useSignatureStateManager";
import { useSignaturePendingAssignments } from "./hooks/useSignaturePendingAssignments";
import { useSignatureAssignedItemsPanelState } from "./hooks/useSignatureAssignedItemsPanelState";
import {
  useSignatureInventoryBatchOps,
  commitInventoryFirstTransferInChunks,
} from "./hooks/useSignatureInventoryBatchOps";
import { db } from "../../config/firebase";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  writeBatch,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import {
  getOrCreatePageId,
  updateEmployeePage,
  checkIfPageCreationAllowed,
} from "../../utils/employeePages";
import { getAssignmentUrl } from "../../config/urls";
import { logActivity, ACTION_TYPES } from "../../utils/historyLogger";
import { normalizeItemSearchText } from "../../utils/itemSearchText";
import { sendAssignmentSms } from "../../services/smsService";
import PersonnelSearch from "./components/PersonnelSearch";
import AddItemModal from "./components/AddItemModal";
import ItemSearchInline from "./components/ItemSearchInline";
import KitSection from "./components/KitSection";
import CartSection from "./components/CartSection";
import SignatureModal from "./components/SignatureModal";
import SignatureTransferModal from "./components/SignatureTransferModal";
import PersonnelConfirmModal from "./components/PersonnelConfirmModal";
import UrlModal from "./components/UrlModal";
import SignedAssignedItemsPanel from "./components/SignedAssignedItemsPanel";
import EmployeeDetailsCollapsible from "./components/EmployeeDetailsCollapsible";
import PendingAssignmentsList from "./components/PendingAssignmentsList";
import InventoryForm from "../../components/forms/InventoryForm";
import { formatPersonDisplayName } from "../../utils/personnelDisplay";

/**
 * When the cart is returns-only, transfer only inventory rows that match those cart lines.
 * When the cart is empty, transfer every assignment row for the selected soldier.
 * Uses Maps / grouping so cart×inventory stays ~O(n+m) instead of O(cart×assigned).
 */
function assignedInventoryRowsForTransfer(assignedItems, cart, allCartReturns) {
  if (!assignedItems?.length) return [];
  if (!allCartReturns || !cart?.length) return assignedItems;

  const byId = new Map();
  const bySerialNorm = new Map();
  const bulkRowsByDefId = new Map();

  for (let i = 0; i < assignedItems.length; i++) {
    const r = assignedItems[i];
    byId.set(r.id, r);
    const sn = r.serialNumber && String(r.serialNumber).trim();
    if (sn) {
      if (!bySerialNorm.has(sn)) bySerialNorm.set(sn, r);
    } else if (r.itemDefinitionId) {
      let list = bulkRowsByDefId.get(r.itemDefinitionId);
      if (!list) {
        list = [];
        bulkRowsByDefId.set(r.itemDefinitionId, list);
      }
      list.push(r);
    }
  }

  const ids = new Set();
  const organicMatches = (c, r) =>
    c.isOrganic === r.isOrganic ||
    (c.isOrganic == null && r.isOrganic == null);

  for (let j = 0; j < cart.length; j++) {
    const c = cart[j];
    if ((c.quantity || 0) >= 0) continue;

    let row = byId.get(c.id);
    if (row) {
      ids.add(row.id);
      continue;
    }

    const serial = c.serial && String(c.serial).trim();
    if (c.hasSerialNumber || serial) {
      if (serial) row = bySerialNorm.get(serial);
      if (row) ids.add(row.id);
      continue;
    }

    const candidates = bulkRowsByDefId.get(c.itemDefinitionId);
    if (candidates) {
      for (let k = 0; k < candidates.length; k++) {
        const r = candidates[k];
        if (organicMatches(c, r)) {
          ids.add(r.id);
          break;
        }
      }
    }
  }

  if (ids.size === 0) return [];
  if (ids.size === assignedItems.length) return assignedItems;

  const out = [];
  for (let i = 0; i < assignedItems.length; i++) {
    const r = assignedItems[i];
    if (ids.has(r.id)) out.push(r);
  }
  return out;
}

/** Same row resolution as useSignatureInventoryBatchOps.handleAssignItem (must stay in sync). */
function resolveSignatureBulkInventoryRowId(
  cartItem,
  inventory,
  isChildUser,
  currentUser,
  targetPersonalNumber,
) {
  if (cartItem.hasSerialNumber) {
    return cartItem.id;
  }
  if (isChildUser && currentUser.employeeId) {
    const assignedItem = inventory.find(
      (invItem) =>
        invItem.itemDefinitionId === cartItem.itemDefinitionId &&
        invItem.assignments?.first?.[currentUser.employeeId] &&
        (cartItem.isOrganic === null ||
          Boolean(invItem.isOrganic) === Boolean(cartItem.isOrganic)),
    );
    return assignedItem?.id || cartItem.id;
  }

  const pn =
    targetPersonalNumber != null ? String(targetPersonalNumber).trim() : "";
  if (pn && cartItem.id) {
    const rowByCartId = inventory.find((invItem) => invItem.id === cartItem.id);
    if (
      rowByCartId &&
      rowByCartId.itemDefinitionId === cartItem.itemDefinitionId &&
      (cartItem.isOrganic === null ||
        Boolean(rowByCartId.isOrganic) === Boolean(cartItem.isOrganic)) &&
      (rowByCartId.assignments?.first?.[pn]?.quantity ?? 0) > 0
    ) {
      return cartItem.id;
    }
  }

  const availableItem = inventory.find(
    (invItem) =>
      invItem.itemDefinitionId === cartItem.itemDefinitionId &&
      (invItem.availableQuantity ?? 0) > 0 &&
      (cartItem.isOrganic === null ||
        Boolean(invItem.isOrganic) === Boolean(cartItem.isOrganic)),
  );
  return availableItem?.id || cartItem.id;
}

function isSignatureCartSerialLine(cartItem) {
  return (
    cartItem.hasSerialNumber === true ||
    (cartItem.serial && String(cartItem.serial).trim() !== "")
  );
}

/**
 * Merge cart lines that target the same inventory row so each row is updated once with the net delta.
 * Without this, a second line often keeps originalQuantity from before the first line was added and
 * overwrites the assignment (e.g. +4 then +1 leaves quantity 1 instead of 5).
 */
function mergeSignatureCartForBatchOperations(
  cart,
  inventory,
  isChildUser,
  currentUser,
  targetPersonalNumber,
) {
  const order = [];
  const map = new Map();

  for (const cartItem of cart) {
    const diff = cartItem.quantity || 0;
    if (diff === 0) continue;

    const key = isSignatureCartSerialLine(cartItem)
      ? `serial:${cartItem.id}`
      : `bulk:${resolveSignatureBulkInventoryRowId(
          cartItem,
          inventory,
          isChildUser,
          currentUser,
          targetPersonalNumber,
        )}`;

    const existing = map.get(key);
    if (!existing) {
      map.set(key, { ...cartItem, quantity: diff });
      order.push(key);
    } else {
      existing.quantity += diff;
      const o0 = existing.originalQuantity ?? 0;
      const o1 = cartItem.originalQuantity ?? 0;
      existing.originalQuantity = Math.min(o0, o1);
    }
  }

  return order
    .map((k) => map.get(k))
    .filter((ci) => (ci.quantity || 0) !== 0);
}

const SignatureTabContent = forwardRef(
  function SignatureTabContent(props, ref) {
    const {
      tabId,
      inventory,
      addInventoryItem,
      updateInventoryItem,
      refreshInventory,
      items,
      units,
      personnel,
      kits,
      addKit,
      updateKit,
      deleteKit,
      reorderKits,
      user,
      isMobile,
      onTabStateChange,
    } = props;

    const location = useLocation();
    const locationStateApplied = useRef(false);
    const [detailsCollapsed, setDetailsCollapsed] = useState(false);

    // Auto-collapse on mobile, expand on desktop
    useEffect(() => {
      setDetailsCollapsed(isMobile);
    }, [isMobile]);

    // Get childUser's personal number and unit ID (optimized)
    const childUserData = useMemo(() => {
      if (!user || user?.permissions !== "childUser" || !user?.employeeId) {
        return { personalNumber: null, unitId: null };
      }

      const employeeId = user.employeeId;
      const currentUserPersonnel = personnel.find(
        (p) => p.personalNumber === employeeId,
      );
      return {
        personalNumber: employeeId,
        unitId: currentUserPersonnel?.unitId || null,
      };
    }, [user, personnel]);

    const { personalNumber: childUserPersonalNumber, unitId: childUserUnitId } =
      childUserData;

    // Memoized currentUser object (used throughout component)
    const currentUser = useMemo(
      () => ({
        id: user?.email || user?.id || "",
        email: user?.email || "",
        permissions: user?.permissions || "childUser",
        employeeId: user?.employeeId || "",
      }),
      [user],
    );

    const isChildUser = useMemo(
      () => currentUser.permissions === "childUser",
      [currentUser.permissions],
    );

    // Filter personnel for childUser to only show employees from their unit
    const availablePersonnel = useMemo(() => {
      if (!personnel?.length) return [];

      if (user?.permissions === "childUser" && childUserUnitId) {
        return personnel.filter((person) => person.unitId === childUserUnitId);
      }

      return personnel;
    }, [personnel, user, childUserUnitId]);

    // Optimized state management
    const {
      // Modal state
      showAddItemModal,
      showSignatureModal,
      showPersonnelConfirmModal,
      openAddItemModal,
      closeAddItemModal,
      openSignatureModal,
      closeSignatureModal,
      openPersonnelConfirmModal,
      closePersonnelConfirmModal,
      closeAllModals,

      // Form state
      itemSearchTerm,
      selectedItem,
      showItemOptions,
      serialSearchTerm,
      selectedSerial,
      showSerialOptions,
      quantity,
      note,
      resetAddItemForm,
      handleItemSelect,
      handleSerialSelect,
      handleItemInputChange,
      handleSerialInputChange,
      handleQuantityChange,
      handleNoteChange,
      handleItemInputFocus,
      handleItemInputBlur,
      handleSerialInputFocus,
      handleSerialInputBlur,

      // Signature state
      signaturePadRef,
      setSignaturePadRef,
      personnelSignaturePadRef,
      setPersonnelSignaturePadRef,
      hasSignature,
      setHasSignature,
      hasPersonnelSignature,
      setHasPersonnelSignature,
      signatureData,
      setSignatureData,
      personnelSignature,
      setPersonnelSignature,
      clearSignature,
      clearMainSignature,
      clearPersonnelSignature,
      getSignatureData,
      resetAllSignatures,

      // Cart state
      cart,
      setCart,
      editingQuantities,
      setEditingQuantities,
      pendingAssignments,
      setPendingAssignments,
      selectedAssignmentId,
      setSelectedAssignmentId,
      addToCart,
      removeFromCart,
      updateCartQuantity,
      updateCartNote,
      clearCart,
      addPendingAssignment,
      removePendingAssignment,
      clearPendingAssignments,
      startEditingQuantity,
      stopEditingQuantity,
      clearAllEditingQuantities,
      resetAllCarts,

      // Search state
      searchTerm,
      selectedEmployee,
      showSearchResults,
      debouncedSearchTerm,
      handleSearchChange,
      handleEmployeeSelect,
      handleClearSelection,
      handleSearchFocus,
      handleSearchBlur,
      resetSearch,

      // Combined utilities
      resetAllState,
    } = useSignatureStateManager();

    /** Prefill for add-to-stock overlay (same tab; no navigation). */
    const [signatureStockFormPrefill, setSignatureStockFormPrefill] =
      useState(null);
    const reopenAddItemAfterStockRef = useRef(false);

    const handleSignatureAddStockSubmit = useCallback(
      async (formData) => {
        try {
          if (formData.validateSerialNumber && formData.serialNumber) {
            const existingSerialItem = inventory.find(
              (item) => item.serialNumber === formData.serialNumber,
            );
            if (existingSerialItem) {
              alert(`מספר סידורי ${formData.serialNumber} כבר קיים במערכת`);
              return;
            }
          }

          if (
            formData.validateQuantityMerge &&
            !formData.serialNumber &&
            !formData.isEdit
          ) {
            const existingItem = inventory.find(
              (item) =>
                item.itemDefinitionId === formData.itemDefinitionId &&
                item.isOrganic === formData.isOrganic &&
                !item.serialNumber &&
                item.status === "available",
            );

            if (existingItem) {
              const confirmMerge = window.confirm(
                `פריט זה כבר קיים במערכת עם כמות ${existingItem.quantity}. האם ברצונך להוסיף ${formData.quantity} לכמות הקיימת?`,
              );

              if (confirmMerge) {
                const updatedQuantity =
                  existingItem.quantity + formData.quantity;
                const oldAvailable =
                  existingItem.availableQuantity ?? existingItem.quantity;
                const newAvailable = oldAvailable + formData.quantity;
                const result = await updateInventoryItem(existingItem.id, {
                  ...existingItem,
                  quantity: updatedQuantity,
                  availableQuantity: newAvailable,
                });

                if (result.success) {
                  setSignatureStockFormPrefill(null);
                  await refreshInventory();
                  const reopen = reopenAddItemAfterStockRef.current;
                  reopenAddItemAfterStockRef.current = false;
                  if (reopen) openAddItemModal();
                  alert(
                    `הפריט עודכן בהצלחה. כמות חדשה: ${updatedQuantity}`,
                  );
                } else {
                  alert(`שגיאה בעדכון הפריט: ${result.error}`);
                }
                return;
              }
              return;
            }
          }

          const result = await addInventoryItem(formData);

          if (result.success) {
            setSignatureStockFormPrefill(null);
            await refreshInventory();
            const reopen = reopenAddItemAfterStockRef.current;
            reopenAddItemAfterStockRef.current = false;
            if (reopen) openAddItemModal();
          } else {
            alert(`שגיאה: ${result.error}`);
          }
        } catch (error) {
          console.error("Error in signature add-stock submit:", error);
          alert(`שגיאה: ${error.message}`);
        }
      },
      [
        inventory,
        addInventoryItem,
        updateInventoryItem,
        refreshInventory,
        openAddItemModal,
      ],
    );

    const handleSignatureAddStockCancel = useCallback(() => {
      const reopen = reopenAddItemAfterStockRef.current;
      reopenAddItemAfterStockRef.current = false;
      setSignatureStockFormPrefill(null);
      if (reopen) openAddItemModal();
    }, [openAddItemModal]);

    /** Open add-to-stock overlay on this tab (keeps soldier/cart state). */
    const goAddStockShortcut = useCallback(
      (opts = {}) => {
        if (
          user?.permissions !== "admin" &&
          user?.permissions !== "parentsUser"
        ) {
          return;
        }
        reopenAddItemAfterStockRef.current = !!showAddItemModal;
        closeAddItemModal();
        const serialNumber = String(opts.serialNumber || "").trim();
        const itemDefinitionId = opts.itemDefinitionId || "";
        setSignatureStockFormPrefill({
          itemDefinitionId,
          serialNumber,
          quantity: 1,
          isOrganic: null,
          currentUnitId: "",
        });
      },
      [user?.permissions, closeAddItemModal, showAddItemModal],
    );

    // Expose reset() to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        reset: resetAllState,
        getSelectedEmployee: () => selectedEmployee,
      }),
      [resetAllState, selectedEmployee],
    );

    const {
      assignedItems,
      assignedItemsFilter,
      setAssignedItemsFilter,
      filteredAssignedItems,
    } = useSignatureAssignedItemsPanelState({
      selectedEmployee,
      inventory,
      items,
      isChildUser,
      currentUser,
    });

    /** quantity > 0 = assigning new stock to the soldier; transfer only moves gear already on the soldier. Returns use quantity < 0. */
    const cartHasForwardAssignments = useMemo(
      () => (cart || []).some((c) => (c.quantity || 0) > 0),
      [cart],
    );

    const showSignatureTransferButton = useMemo(
      () =>
        !isChildUser &&
        !!selectedEmployee &&
        (assignedItems?.length || 0) > 0 &&
        !cartHasForwardAssignments,
      [
        isChildUser,
        selectedEmployee,
        assignedItems?.length,
        cartHasForwardAssignments,
      ],
    );

    /** All lines are returns (זיכוי) — transfer can sit on the same row as המשך לחתימה */
    const allCartItemsAreReturns = useMemo(
      () => (cart?.length || 0) > 0 && cart.every((c) => (c.quantity || 0) < 0),
      [cart],
    );

    const inventoryRowsForSignatureTransfer = useMemo(
      () =>
        assignedInventoryRowsForTransfer(
          assignedItems,
          cart,
          allCartItemsAreReturns,
        ),
      [assignedItems, cart, allCartItemsAreReturns],
    );

    const showTransferSignaturesInCartFooter = useMemo(
      () =>
        showSignatureTransferButton &&
        allCartItemsAreReturns &&
        inventoryRowsForSignatureTransfer.length > 0,
      [
        showSignatureTransferButton,
        allCartItemsAreReturns,
        inventoryRowsForSignatureTransfer.length,
      ],
    );

    const { fetchPendingAssignments } = useSignaturePendingAssignments({
      currentUser,
      personnel,
      setPendingAssignments,
    });

    const { handleAssignItem, handleReturnItem } =
      useSignatureInventoryBatchOps({
        inventory,
        currentUser,
        isChildUser,
      });

    // Syncs tab UI state with the parent component.
    // Relies on onTabStateChange being a stable callback passed from the parent.
    useEffect(() => {
      if (!onTabStateChange || tabId == null) return;
      const name = selectedEmployee
        ? formatPersonDisplayName(selectedEmployee)
        : "";
      onTabStateChange(tabId, {
        employeeName: name,
        hasContent: !!(selectedEmployee || cart.length > 0),
        personalNumber: selectedEmployee?.personalNumber,
      });
    }, [selectedEmployee, cart, onTabStateChange, tabId]);

    // Pre-select employee when navigated from Inventory page (only on first mount)
    useEffect(() => {
      if (location.state?.employee && !locationStateApplied.current) {
        locationStateApplied.current = true;
        handleEmployeeSelect(location.state.employee);
      }
    }, []);

    // Cleanup debounce timers on unmount
    useEffect(() => {
      return () => {
        Object.values(debounceTimersRef.current).forEach((timer) => {
          clearTimeout(timer);
        });
        debounceTimersRef.current = {};
      };
    }, []);

    // Restore focus to active input after re-render
    useEffect(() => {
      const activeId = activeInputRef.current?.id;
      if (!activeId) return;

      const rafId = requestAnimationFrame(() => {
        const inputEl = document.getElementById(`quantity-${activeId}`);
        if (inputEl && document.activeElement !== inputEl) {
          inputEl.focus();
          const pos = activeInputRef.current.position;
          if (pos != null && pos >= 0) {
            inputEl.setSelectionRange(pos, pos);
          }
        }
      });

      return () => cancelAnimationFrame(rafId);
    }, [editingQuantities]);

    // Initialize signature pad canvas
    useEffect(() => {
      const initializeCanvas = (canvas) => {
        if (canvas) {
          const ctx = canvas.getContext("2d");
          ctx.strokeStyle = "#000";
          ctx.lineWidth = 2;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
        }
      };

      if (signaturePadRef) {
        initializeCanvas(signaturePadRef);
      }
      if (personnelSignaturePadRef) {
        initializeCanvas(personnelSignaturePadRef);
      }
    }, [signaturePadRef, personnelSignaturePadRef]);

    // Helper functions
    const getUnitName = useCallback(
      (unitId) => {
        if (!unitId || !units?.length) return "לא מוגדר";
        const unit = units.find((u) => u.id === unitId);
        return unit?.unitName || "לא מוגדר";
      },
      [units],
    );

    const formatBooleanValue = useCallback((value) => {
      if (value === true) return "כן";
      if (value === false) return "לא";
      return "לא מוגדר";
    }, []);

    // URL Modal state
    const [showUrlModal, setShowUrlModal] = useState(false);
    const [urlModalData, setUrlModalData] = useState({ url: "", message: "" });

    const [showTransferPicker, setShowTransferPicker] = useState(false);
    const [transferTarget, setTransferTarget] = useState(null);
    const [showTransferSignatureModal, setShowTransferSignatureModal] =
      useState(false);

    const handleUrlModalClose = useCallback(() => {
      setShowUrlModal(false);
    }, []);

    // Ref to store debounce timers for each item
    const debounceTimersRef = useRef({});
    // Ref to track currently focused input (to restore focus after re-render)
    const activeInputRef = useRef({ id: null, position: null });
    // Ref for the filter input to maintain focus
    const filterInputRef = useRef(null);
    // Track if filter input should maintain focus
    const shouldMaintainFilterFocus = useRef(false);
    // Guard to prevent duplicate SMS on a single sign action
    const smsSentRef = useRef(false);

    // Restore focus to filter input after re-render if it had focus
    useEffect(() => {
      if (!filterInputRef.current || !shouldMaintainFilterFocus.current) return;

      const rafId = requestAnimationFrame(() => {
        if (
          filterInputRef.current &&
          shouldMaintainFilterFocus.current &&
          document.activeElement !== filterInputRef.current
        ) {
          filterInputRef.current.focus();
          const pos = filterInputRef.current.value.length;
          filterInputRef.current.setSelectionRange(pos, pos);
        }
      });

      return () => cancelAnimationFrame(rafId);
    }, [assignedItemsFilter]);

    // Return to stock handlers
    const handleReturnQuantityChange = useCallback((itemId, newQuantity) => {
      setEditingQuantities((prev) => ({
        ...prev,
        [itemId]: newQuantity,
      }));
    }, []);

    // Function to get available quantity for an item
    const getAvailableQuantityForItem = useCallback(
      (item) => {
        if (!inventory?.length) return 0;

        const availableItems = inventory.filter(
          (invItem) =>
            invItem.itemDefinitionId === item.itemDefinitionId &&
            invItem.status === "available" &&
            (item.isOrganic === null || invItem.isOrganic === item.isOrganic),
        );

        const totalAvailable = availableItems.reduce(
          (sum, invItem) => sum + invItem.quantity,
          0,
        );

        return totalAvailable;
      },
      [inventory],
    );

    const handleEditQuantity = useCallback(
      (item, overrideQuantity = null) => {
        const newQuantity =
          overrideQuantity !== null
            ? overrideQuantity
            : editingQuantities[item.id] || item.assignedQuantity;
        const originalQuantity = item.assignedQuantity;
        const difference = newQuantity - originalQuantity;

        if (difference > 0) {
          let availableQuantity;

          if (isChildUser && item.availableQuantity !== undefined) {
            availableQuantity = item.availableQuantity;
          } else {
            availableQuantity = item.availableQuantity ?? 0;
          }

          const maxQuantity = originalQuantity + availableQuantity;

          if (newQuantity > maxQuantity) {
            const message =
              isChildUser && item.availableQuantity !== undefined
                ? `הכמות המקסימלית היא ${maxQuantity} (${originalQuantity} מוקצית + ${availableQuantity} זמין)`
                : `אין מספיק פריטים זמינים במלאי. זמין: ${availableQuantity}, נדרש: ${difference}`;

            alert(message);

            setEditingQuantities((prev) => ({
              ...prev,
              [item.id]: maxQuantity,
            }));

            setCart((prev) => prev.filter((ci) => ci.id !== item.id));
            return;
          }
        }

        if (difference !== 0) {
          const existingCartItem = cart.find((ci) => ci.id === item.id);

          if (existingCartItem) {
            setCart((prev) =>
              prev.map((ci) =>
                ci.id === item.id
                  ? {
                      ...ci,
                      quantity: difference,
                      item: item.itemName || "",
                      serial: item.serialNumber || "",
                      itemDefinitionId: item.itemDefinitionId || "",
                      hasSerialNumber: item.isSerialItem || false,
                      isOrganic: item.isOrganic ?? null,
                      originalQuantity: originalQuantity,
                      finalQuantity: newQuantity,
                      availableQuantity: item.availableQuantity ?? 0,
                    }
                  : ci,
              ),
            );
          } else {
            const cartItem = {
              id: item.id || "",
              item: item.itemName || "",
              serial: item.serialNumber || "",
              quantity: difference,
              itemDefinitionId: item.itemDefinitionId || "",
              hasSerialNumber: item.isSerialItem || false,
              isOrganic: item.isOrganic ?? null,
              originalQuantity: originalQuantity,
              finalQuantity: newQuantity,
              availableQuantity: item.availableQuantity ?? 0,
            };

            setCart((prev) => [...prev, cartItem]);
          }
        } else {
          setCart((prev) => prev.filter((ci) => ci.id !== item.id));
        }
      },
      [editingQuantities, cart, getAvailableQuantityForItem, isChildUser],
    );

    // Debounced cart update handler
    const debouncedHandleEditQuantity = useCallback(
      (item, newQuantity) => {
        if (debounceTimersRef.current[item.id]) {
          clearTimeout(debounceTimersRef.current[item.id]);
        }

        debounceTimersRef.current[item.id] = setTimeout(() => {
          handleEditQuantity(item, newQuantity);
          delete debounceTimersRef.current[item.id];
        }, 800);
      },
      [handleEditQuantity],
    );

    // Cart handlers that reset editing quantities
    const handleClearCart = useCallback(() => {
      clearCart();
      setEditingQuantities({});
    }, [clearCart]);

    const handleRemoveFromCart = useCallback(
      (itemId) => {
        removeFromCart(itemId);
        setEditingQuantities((prev) => {
          const newState = { ...prev };
          delete newState[itemId];
          return newState;
        });
      },
      [removeFromCart],
    );

    const handleCartQuantityChange = useCallback(
      (itemId, newDifference) => {
        const cartItem = cart.find((ci) => ci.id === itemId);
        if (!cartItem) return;

        const originalQuantity = cartItem.originalQuantity || 0;
        const finalQuantity = originalQuantity + newDifference;

        if (newDifference < 0) {
          const returnQuantity = Math.abs(newDifference);
          if (returnQuantity > originalQuantity) {
            alert(
              `לא ניתן להחזיר יותר מהכמות המוקצית. מוקצית: ${originalQuantity}, מנסה להחזיר: ${returnQuantity}`,
            );
            return;
          }
        }

        updateCartQuantity(itemId, newDifference);

        setEditingQuantities((prev) => ({
          ...prev,
          [itemId]: finalQuantity,
        }));
      },
      [updateCartQuantity, cart],
    );

    // Get unique item names from inventory (filtered for childUser)
    const availableItemNames = useMemo(() => {
      if (!inventory?.length || !items?.length) {
        return [];
      }

      const itemMap = new Map();
      items.forEach((item) => {
        itemMap.set(item.id, item);
      });

      const uniqueItems = new Map();
      inventory.forEach((invItem) => {
        if (isChildUser && childUserPersonalNumber) {
          const firstAssignment =
            invItem.assignments?.first?.[childUserPersonalNumber];
          if (!firstAssignment || firstAssignment.quantity <= 0) {
            return;
          }

          const itemDef = itemMap.get(invItem.itemDefinitionId);
          if (!itemDef) return;

          let availableQuantity = 0;
          if (itemDef.isChecked) {
            const secondAssignment =
              invItem.assignments?.second?.[childUserPersonalNumber];
            if (!secondAssignment) {
              availableQuantity = 1;
            }
          } else {
            const firstQuantity = firstAssignment.quantity || 0;
            const secondAssignments = invItem.assignments?.second;
            let secondSum = 0;
            if (secondAssignments) {
              secondSum = Object.values(secondAssignments).reduce(
                (sum, assignment) => sum + (assignment.quantity || 0),
                0,
              );
            }
            availableQuantity = Math.max(0, firstQuantity - secondSum);
          }

          if (availableQuantity > 0) {
            const key = itemDef.isChecked
              ? itemDef.itemName
              : `${itemDef.itemName} (${invItem.isOrganic ? "אורגני" : "קווי"})`;

            if (!uniqueItems.has(key)) {
              uniqueItems.set(key, {
                itemName: itemDef.itemName,
                itemDefinitionId: itemDef.id,
                isChecked: itemDef.isChecked,
                availableQuantity: availableQuantity,
                isOrganic: invItem.isOrganic,
              });
            } else {
              const existingItem = uniqueItems.get(key);
              uniqueItems.set(key, {
                ...existingItem,
                availableQuantity:
                  existingItem.availableQuantity + availableQuantity,
              });
            }
          }
        } else {
          const itemDef = itemMap.get(invItem.itemDefinitionId);
          if (itemDef) {
            const key = itemDef.isChecked
              ? itemDef.itemName
              : `${itemDef.itemName} (${invItem.isOrganic ? "אורגני" : "קווי"})`;

            let availableQuantity = 0;
            if (invItem.serialNumber) {
              if (invItem.status === "available") {
                availableQuantity = 1;
              }
            } else {
              availableQuantity = invItem.availableQuantity ?? 0;
            }

            if (availableQuantity > 0 || invItem.serialNumber) {
              if (!uniqueItems.has(key)) {
                uniqueItems.set(key, {
                  itemName: itemDef.itemName,
                  itemDefinitionId: itemDef.id,
                  isChecked: itemDef.isChecked,
                  availableQuantity: availableQuantity,
                  isOrganic: invItem.isOrganic,
                });
              } else {
                const existingItem = uniqueItems.get(key);
                uniqueItems.set(key, {
                  ...existingItem,
                  availableQuantity:
                    existingItem.availableQuantity + availableQuantity,
                });
              }
            }
          }
        }
      });

      return Array.from(uniqueItems.values());
    }, [inventory, items, isChildUser, childUserPersonalNumber]);

    // Filter available items based on search
    const filteredItems = useMemo(() => {
      if (!itemSearchTerm.trim()) return [];
      const term = normalizeItemSearchText(itemSearchTerm);
      return availableItemNames.filter((item) =>
        normalizeItemSearchText(item.itemName).includes(term),
      );
    }, [availableItemNames, itemSearchTerm]);

    // Get available serial numbers for selected item (filtered for childUser)
    const availableSerials = useMemo(() => {
      if (!selectedItem || !inventory?.length) return [];

      const itemDefinitionId = selectedItem.itemDefinitionId;

      if (isChildUser && childUserPersonalNumber) {
        return inventory
          .filter((invItem) => {
            if (invItem.itemDefinitionId !== itemDefinitionId) return false;
            if (!invItem.serialNumber || invItem.serialNumber.trim() === "")
              return false;

            const firstAssignment =
              invItem.assignments?.first?.[childUserPersonalNumber];
            if (!firstAssignment) return false;

            const secondAssignment =
              invItem.assignments?.second?.[childUserPersonalNumber];
            return !secondAssignment;
          })
          .map((invItem) => invItem.serialNumber);
      }

      return inventory
        .filter(
          (invItem) =>
            invItem.itemDefinitionId === itemDefinitionId &&
            invItem.status === "available" &&
            invItem.serialNumber &&
            invItem.serialNumber.trim() !== "",
        )
        .map((invItem) => invItem.serialNumber);
    }, [selectedItem, inventory, isChildUser, childUserPersonalNumber]);

    // Filter serial numbers based on search
    const filteredSerials = useMemo(() => {
      if (!serialSearchTerm.trim()) return availableSerials;
      const term = normalizeItemSearchText(serialSearchTerm);
      return availableSerials.filter((serial) =>
        normalizeItemSearchText(serial).includes(term),
      );
    }, [availableSerials, serialSearchTerm]);

    // Optimized search filter
    const filteredPersonnel = useMemo(() => {
      if (!debouncedSearchTerm.trim() || !personnel?.length) return [];

      const term = debouncedSearchTerm.toLowerCase().trim();
      const searchWords = term.split(/\s+/).filter((word) => word.length > 0);

      return personnel.filter((person) => {
        const firstName = person.firstName?.toLowerCase() || "";
        const lastName = person.lastName?.toLowerCase() || "";
        const nickname = (person.nickname || "").toLowerCase().trim();
        const personalNumber = person.personalNumber?.toString() || "";
        const fullName = `${firstName} ${lastName}`.trim();

        if (searchWords.length === 1) {
          return (
            firstName.includes(term) ||
            lastName.includes(term) ||
            nickname.includes(term) ||
            personalNumber.includes(term) ||
            fullName.includes(term)
          );
        }

        return searchWords.every(
          (word) =>
            firstName.includes(word) ||
            lastName.includes(word) ||
            nickname.includes(word) ||
            personalNumber.includes(word),
        );
      });
    }, [personnel, debouncedSearchTerm]);

    const handleAddItem = useCallback(
      (cartItem) => {
        if (!cartItem || !selectedEmployee) return;

        if (cartItem.serial) {
          const existingSerialItem = cart.find(
            (item) => item.serial === cartItem.serial,
          );
          if (existingSerialItem) {
            alert("מספר סידורי זה כבר קיים בעגלה");
            return;
          }
        } else {
          const existingNonSerialItem = cart.find(
            (item) =>
              !item.serial &&
              item.itemDefinitionId === cartItem.itemDefinitionId &&
              item.isOrganic === cartItem.isOrganic,
          );
          if (existingNonSerialItem) {
            setCart((prevCart) =>
              prevCart.map((item) =>
                item.id === existingNonSerialItem.id
                  ? {
                      ...item,
                      quantity: item.quantity + cartItem.quantity,
                    }
                  : item,
              ),
            );
            closeAddItemModal();
            return;
          }
        }

        setCart((prevCart) => [...prevCart, cartItem]);
        closeAddItemModal();
      },
      [selectedEmployee, cart, closeAddItemModal],
    );

    // Handle adding return items to cart with actual assigned quantity
    const handleAddReturnItem = useCallback(
      (item) => {
        if (!selectedEmployee) return;

        const fullReturnQuantity = -Math.abs(item.assignedQuantity);
        const assignedQuantity = Math.abs(item.assignedQuantity);

        setEditingQuantities((prev) => ({
          ...prev,
          [item.id]: 0,
        }));

        const isSerialItem =
          !!item.serialNumber && item.serialNumber.trim() !== "";

        const existingCartItem = cart.find((cartItem) => {
          if (cartItem.id === item.id) return true;

          if (isSerialItem) {
            return false;
          } else {
            return (
              (cartItem.itemDefinitionId === item.itemDefinitionId ||
                cartItem.id === item.id) &&
              cartItem.isOrganic === item.isOrganic &&
              (!cartItem.serial || cartItem.serial === "")
            );
          }
        });

        if (existingCartItem) {
          setCart((prevCart) =>
            prevCart.map((cartItem) => {
              let isMatch = false;
              if (cartItem.id === item.id) {
                isMatch = true;
              } else if (!isSerialItem) {
                isMatch =
                  (cartItem.itemDefinitionId === item.itemDefinitionId ||
                    cartItem.id === item.id) &&
                  cartItem.isOrganic === item.isOrganic &&
                  (!cartItem.serial || cartItem.serial === "");
              }

              return isMatch
                ? {
                    ...cartItem,
                    quantity: fullReturnQuantity,
                    isReturn: true,
                    originalQuantity: assignedQuantity,
                    itemDefinitionId:
                      item.itemDefinitionId || cartItem.itemDefinitionId,
                    isOrganic: item.isOrganic ?? cartItem.isOrganic ?? null,
                  }
                : cartItem;
            }),
          );
        } else {
          const returnCartItem = {
            id: item.id || Date.now().toString(),
            item: item.itemName,
            serial: item.serialNumber || "",
            quantity: fullReturnQuantity,
            itemDefinitionId: item.itemDefinitionId,
            hasSerialNumber: !!item.serialNumber,
            isReturn: true,
            isOrganic: item.isOrganic,
            originalQuantity: assignedQuantity,
          };

          setCart((prevCart) => [...prevCart, returnCartItem]);
        }
      },
      [selectedEmployee, cart, setEditingQuantities],
    );

    const handleFinalizeAssignment = useCallback(() => {
      if (!selectedEmployee || cart.length === 0) return;
      openSignatureModal();
    }, [selectedEmployee, cart, openSignatureModal]);

    const handleOpenTransferClick = useCallback(() => {
      if (isChildUser) return;
      if (!selectedEmployee?.personalNumber) return;
      if (cartHasForwardAssignments) {
        alert(
          "נא להסיר מהעגלה פריטים להקצאה חדשה לפני החלפת חתימות. ניתן להשאיר בעגלה רק זיכויים (החזרות) של ציוד שכבר מוחתם לחייל.",
        );
        return;
      }
      if (!inventoryRowsForSignatureTransfer.length) {
        if (allCartItemsAreReturns && cart.length > 0) {
          alert(
            "לא נמצאה התאמה בין הפריטים בעגלה לבין השורות המוחתמות לחייל. נא לרענן ולנסות שוב.",
          );
        } else {
          alert("אין פריטים מוחתמים להעברה לחייל זה");
        }
        return;
      }
      setShowTransferPicker(true);
    }, [
      isChildUser,
      selectedEmployee,
      cartHasForwardAssignments,
      inventoryRowsForSignatureTransfer,
      allCartItemsAreReturns,
      cart.length,
    ]);

    const handleTransferPickerClose = useCallback(() => {
      setShowTransferPicker(false);
    }, []);

    const handleContinueTransferToSignature = useCallback(
      (target) => {
        if (!target?.personalNumber) return;
        if (target.personalNumber === selectedEmployee?.personalNumber) {
          alert("לא ניתן לבחור את אותו חייל");
          return;
        }
        setTransferTarget(target);
        setShowTransferPicker(false);
        clearMainSignature();
        setHasSignature(false);
        setShowTransferSignatureModal(true);
      },
      [selectedEmployee, clearMainSignature, setHasSignature],
    );

    const handleTransferSignatureModalClose = useCallback(() => {
      setShowTransferSignatureModal(false);
      setTransferTarget(null);
      clearMainSignature();
      setHasSignature(false);
    }, [clearMainSignature, setHasSignature]);

    const handleSignatureConfirm = useCallback(
      async (providedSignatureDataUrl = null) => {
        let signatureDataUrl = providedSignatureDataUrl;

        if (!signatureDataUrl && signaturePadRef) {
          signatureDataUrl = getSignatureData(signaturePadRef);
        }

        if (
          !signatureDataUrl ||
          !signatureDataUrl.trim() ||
          !signatureDataUrl.startsWith("data:image")
        ) {
          alert("נא להוסיף חתימה לאישור ההקצאה");
          return;
        }

        if (!hasSignature) {
          alert("נא להוסיף חתימה לאישור ההקצאה");
          return;
        }

        try {
          if (!currentUser.email) {
            alert("שגיאה: לא נמצא מידע משתמש");
            return;
          }

          if (!selectedEmployee || !selectedEmployee.personalNumber) {
            alert("שגיאה: לא נמצא מידע חייל נבחר");
            return;
          }

          if (!cart || cart.length === 0) {
            alert("שגיאה: אין פריטים להקצאה");
            return;
          }

          const targetPn = selectedEmployee.personalNumber || "";

          const cartForBatch = mergeSignatureCartForBatchOperations(
            cart,
            inventory,
            isChildUser,
            currentUser,
            targetPn,
          );
          if (!cartForBatch.length) {
            alert("שגיאה: אין פריטים להקצאה");
            return;
          }

          const batch = writeBatch(db);

          const cartAssignmentLines = cartForBatch.map((cartItem) => ({
            quantity: cartItem.quantity || 0,
            assignedAt: new Date(),
          }));
          const childSigning = isChildUser && currentUser.employeeId;

          const assignmentData = {
            personnelId: targetPn,
            personnelName: formatPersonDisplayName(selectedEmployee).trim(),
            signature: signatureDataUrl || "",
            timestamp: serverTimestamp(),
            assignedAt: new Date(),
            assignments: childSigning
              ? {
                  first: null,
                  second: { [targetPn]: cartAssignmentLines },
                }
              : {
                  first: { [targetPn]: cartAssignmentLines },
                  second: null,
                },
            items: cartForBatch.map((cartItem) => ({
              itemId: cartItem.item?.itemDefinitionId || "",
              itemName: cartItem.item?.itemName || "",
              serial: cartItem.serial || "",
              quantity: cartItem.quantity || 0,
              isSerial: cartItem.item?.isChecked || false,
              note: cartItem.note || "",
            })),
          };

          const assignmentRef = doc(collection(db, "assignments"));
          batch.set(assignmentRef, assignmentData);

          for (const cartItem of cartForBatch) {
            const originalQuantity = cartItem.originalQuantity || 0;
            const difference = cartItem.quantity || 0;

            if (difference < 0) {
              await handleReturnItem(
                batch,
                cartItem,
                selectedEmployee,
                Math.abs(difference),
              );
            } else if (difference > 0) {
              await handleAssignItem(
                batch,
                cartItem,
                selectedEmployee,
                difference,
              );
            }
          }

          await batch.commit();

          for (const cartItem of cartForBatch) {
            const difference = cartItem.quantity || 0;
            if (difference === 0) continue;

            const itemName =
              cartItem.item?.itemName ||
              cartItem.item ||
              inventory.find((item) => item.id === cartItem.id)?.itemName ||
              "פריט ללא שם";
            const serialNumber =
              cartItem.serial ||
              inventory.find((item) => item.id === cartItem.id)?.serialNumber ||
              null;

            let inventoryItemId = cartItem.id;
            if (!cartItem.hasSerialNumber) {
              const foundItem = inventory.find((item) => {
                if (item.id === cartItem.id) return true;
                if (
                  item.itemDefinitionId === cartItem.itemDefinitionId &&
                  (cartItem.isOrganic === null ||
                    item.isOrganic === cartItem.isOrganic)
                ) {
                  if (isChildUser && currentUser.employeeId) {
                    return item.assignments?.first?.[currentUser.employeeId];
                  }
                  return item.status === "available";
                }
                return false;
              });
              if (foundItem) {
                inventoryItemId = foundItem.id;
              }
            }

            const assignedToName = (() => {
              const name = formatPersonDisplayName(selectedEmployee).trim();
              if (name) return name;
              const pn = selectedEmployee.personalNumber;
              return pn ? `מס׳ אישי: ${pn}` : "";
            })();

            if (difference > 0) {
              await logActivity({
                actionType: ACTION_TYPES.ASSIGNED,
                itemId: inventoryItemId,
                itemName,
                serialNumber,
                details: {
                  assignedTo: assignedToName,
                  quantity: Math.abs(difference),
                  personalNumber: selectedEmployee.personalNumber,
                },
              });
            } else if (difference < 0) {
              await logActivity({
                actionType: ACTION_TYPES.RETURNED,
                itemId: inventoryItemId,
                itemName,
                serialNumber,
                details: {
                  returnedFrom: assignedToName,
                  quantity: Math.abs(difference),
                  personalNumber: selectedEmployee.personalNumber,
                },
              });
            }
          }

          const assignments = cart.filter((item) => (item.quantity || 0) > 0);
          const returns = cart.filter((item) => (item.quantity || 0) < 0);

          let message = `עסקה הושלמה! `;
          if (assignments.length > 0) {
            message += `${assignments.length} פריטים הוקצו `;
          }
          if (returns.length > 0) {
            message += `${returns.length} פריטים זוכו `;
          }
          message += `לחייל ${formatPersonDisplayName(selectedEmployee)}.`;

          try {
            const canCreate = await checkIfPageCreationAllowed();

            if (canCreate) {
              const deltaItems = cartForBatch
                .filter((item) => item.quantity !== 0)
                .map((cartItem) => ({
                  itemName: cartItem.item?.itemName || cartItem.item || "",
                  quantity: cartItem.quantity || 0,
                  serial: cartItem.serial || "",
                  isSerial: cartItem.hasSerialNumber || false,
                  itemDefinitionId: cartItem.itemDefinitionId || "",
                  inventoryItemId: cartItem.id || "",
                  action: cartItem.quantity > 0 ? "added" : "removed",
                }));

              if (deltaItems.length > 0) {
                let signedBy = "";
                let signedByName = "";

                if (isChildUser && currentUser.employeeId) {
                  signedBy = currentUser.employeeId;
                  const currentPersonnel = personnel.find(
                    (p) => p.personalNumber === signedBy,
                  );
                  if (currentPersonnel) {
                    signedByName =
                      `${currentPersonnel.firstName} ${currentPersonnel.lastName}`.trim();
                  } else {
                    const fallbackPersonnel = personnel.find(
                      (p) => p.email === currentUser.email,
                    );
                    if (fallbackPersonnel) {
                      signedBy = fallbackPersonnel.personalNumber;
                      signedByName =
                        `${fallbackPersonnel.firstName} ${fallbackPersonnel.lastName}`.trim();
                    } else {
                      signedBy = currentUser.employeeId;
                      signedByName = "";
                    }
                  }
                } else {
                  let currentPersonnel = personnel.find(
                    (p) => p.email === currentUser.email,
                  );

                  if (!currentPersonnel && currentUser.employeeId) {
                    currentPersonnel = personnel.find(
                      (p) => p.personalNumber === currentUser.employeeId,
                    );
                  }

                  if (currentPersonnel) {
                    signedBy = currentPersonnel.personalNumber;
                    signedByName =
                      `${currentPersonnel.firstName} ${currentPersonnel.lastName}`.trim();
                  } else {
                    signedBy = currentUser.email || "";
                    signedByName = "";
                  }
                }

                if (!signedBy) {
                  signedBy = currentUser.email || currentUser.employeeId || "";
                }

                const pageId = await getOrCreatePageId(
                  selectedEmployee.id,
                  selectedEmployee.personalNumber,
                  currentUser.email,
                );

                const lastTransaction = {
                  transactionId: assignmentRef.id,
                  signedBy: signedBy,
                  signedByName: signedByName,
                  signedAt: serverTimestamp(),
                  signatureDataUrl: signatureDataUrl,
                  items: deltaItems,
                  createdAt: serverTimestamp(),
                };

                await updateEmployeePage(pageId, lastTransaction);

                const pageUrl = getAssignmentUrl(pageId);

                setUrlModalData({
                  url: pageUrl,
                  message: message,
                });
                setShowUrlModal(true);

                if (selectedEmployee.phone && !smsSentRef.current) {
                  smsSentRef.current = true;
                  sendAssignmentSms(
                    selectedEmployee.phone,
                    formatPersonDisplayName(selectedEmployee),
                    pageUrl,
                    assignments.length > 0,
                    returns.length > 0,
                  ).catch((err) => console.warn("[SMS] send failed:", err));
                }
              } else {
                alert(message);
              }
            } else {
              alert(message);
            }
          } catch (error) {
            console.error("Error updating employee page:", error);
            alert(message);
          }

          await refreshInventory();

          setCart([]);
          closeSignatureModal();
          setSignatureData("");
          setHasSignature(false);
          clearMainSignature();
          smsSentRef.current = false;
        } catch (error) {
          console.error("Error during assignment:", error);
          alert(
            `שגיאה בהקצאת הפריטים: ${
              error.message || "שגיאה לא ידועה"
            }. נא לנסות שוב.`,
          );
        }
      },
      [
        selectedEmployee,
        cart,
        signatureData,
        inventory,
        hasSignature,
        signaturePadRef,
        getSignatureData,
        currentUser,
        personnel,
        isChildUser,
        handleAssignItem,
        handleReturnItem,
        clearMainSignature,
        closeSignatureModal,
        setCart,
        setSignatureData,
        setHasSignature,
        refreshInventory,
      ],
    );

    const transferSignatureModalItems = useMemo(
      () =>
        (inventoryRowsForSignatureTransfer || []).map((row) => ({
          item: row.itemName,
          serial: row.serialNumber || "",
          quantity: row.assignedQuantity,
        })),
      [inventoryRowsForSignatureTransfer],
    );

    const handleTransferSignatureConfirm = useCallback(
      async (providedSignatureDataUrl = null) => {
        let signatureDataUrl = providedSignatureDataUrl;

        if (!signatureDataUrl && signaturePadRef) {
          signatureDataUrl = getSignatureData(signaturePadRef);
        }

        if (
          !signatureDataUrl ||
          !signatureDataUrl.trim() ||
          !signatureDataUrl.startsWith("data:image")
        ) {
          alert("נא להוסיף חתימה לאישור ההעברה");
          return;
        }

        if (!hasSignature) {
          alert("נא להוסיף חתימה לאישור ההעברה");
          return;
        }

        const source = selectedEmployee;
        const target = transferTarget;

        if (!currentUser.email) {
          alert("שגיאה: לא נמצא מידע משתמש");
          return;
        }

        if (!source?.personalNumber || !target?.personalNumber) {
          alert("שגיאה: חסר מידע חייל מקור או יעד");
          return;
        }

        if (target.personalNumber === source.personalNumber) {
          alert("שגיאה: מקור ויעד זהים");
          return;
        }

        const transferRows = inventoryRowsForSignatureTransfer;
        if (!transferRows?.length) {
          alert("אין פריטים להעברה");
          return;
        }

        try {
          const { totalUpdated } = await commitInventoryFirstTransferInChunks({
            rows: transferRows,
            sourcePersonalNumber: source.personalNumber,
            targetEmployee: target,
            currentUser,
          });

          if (totalUpdated === 0) {
            alert(
              "לא בוצעה העברה — ייתכן שהנתונים עודכנו. נא לרענן ולנסות שוב.",
            );
            return;
          }

          const itemsSnapshot = transferRows.map((row) => {
            const src = row.assignments?.first?.[source.personalNumber];
            return {
              itemId: row.itemDefinitionId || "",
              itemName: row.itemName || "",
              serial: row.serialNumber || "",
              quantity: row.assignedQuantity || 0,
              isSerial: !!row.isSerialItem,
              note: src?.note || "",
              inventoryItemId: row.id || "",
            };
          });

          const assignmentPayload = {
            transfer: true,
            fromPersonnelId: source.personalNumber,
            toPersonnelId: target.personalNumber,
            fromPersonnelName:
              `${source.firstName || ""} ${source.lastName || ""}`.trim(),
            toPersonnelName:
              `${target.firstName || ""} ${target.lastName || ""}`.trim(),
            personnelId: target.personalNumber,
            signature: signatureDataUrl,
            timestamp: serverTimestamp(),
            assignedAt: new Date(),
            items: itemsSnapshot,
            performedByEmail: currentUser.email,
          };

          const assignmentRefDoc = await addDoc(
            collection(db, "assignments"),
            assignmentPayload,
          );

          const sourceName = [source.firstName, source.lastName]
            .filter(Boolean)
            .join(" ")
            .trim();
          const targetName = [target.firstName, target.lastName]
            .filter(Boolean)
            .join(" ")
            .trim();

          for (const row of transferRows) {
            const itemName = row.itemName || "פריט ללא שם";
            const serialNumber = row.serialNumber || null;
            const qty = row.assignedQuantity || 0;
            if (qty <= 0) continue;

            await logActivity({
              actionType: ACTION_TYPES.RETURNED,
              itemId: row.id,
              itemName,
              serialNumber,
              details: {
                returnedFrom:
                  sourceName || `מס׳ אישי: ${source.personalNumber}`,
                quantity: qty,
                personalNumber: source.personalNumber,
                transferTo: target.personalNumber,
              },
              metadata: { transfer: true },
            });

            await logActivity({
              actionType: ACTION_TYPES.ASSIGNED,
              itemId: row.id,
              itemName,
              serialNumber,
              details: {
                assignedTo: targetName || `מס׳ אישי: ${target.personalNumber}`,
                quantity: qty,
                personalNumber: target.personalNumber,
                transferFrom: source.personalNumber,
              },
              metadata: { transfer: true },
            });
          }

          const message = `העברת חתימות הושלמה: ${totalUpdated} פריטים מ־${sourceName} אל ${targetName}.`;

          try {
            const canCreate = await checkIfPageCreationAllowed();

            if (canCreate) {
              let signedBy = "";
              let signedByName = "";

              let currentPersonnel = personnel.find(
                (p) => p.email === currentUser.email,
              );

              if (!currentPersonnel && currentUser.employeeId) {
                currentPersonnel = personnel.find(
                  (p) => p.personalNumber === currentUser.employeeId,
                );
              }

              if (currentPersonnel) {
                signedBy = currentPersonnel.personalNumber;
                signedByName =
                  `${currentPersonnel.firstName} ${currentPersonnel.lastName}`.trim();
              } else {
                signedBy = currentUser.email || "";
                signedByName = "";
              }

              if (!signedBy) {
                signedBy = currentUser.email || currentUser.employeeId || "";
              }

              const deltaItems = transferRows.map((row) => ({
                itemName: row.itemName || "",
                quantity: row.assignedQuantity || 0,
                serial: row.serialNumber || "",
                isSerial: !!row.isSerialItem,
                itemDefinitionId: row.itemDefinitionId || "",
                inventoryItemId: row.id || "",
                action: "added",
              }));

              const pageId = await getOrCreatePageId(
                target.id,
                target.personalNumber,
                currentUser.email,
              );

              const lastTransaction = {
                transactionId: assignmentRefDoc.id,
                signedBy,
                signedByName,
                signedAt: serverTimestamp(),
                signatureDataUrl: signatureDataUrl,
                items: deltaItems,
                createdAt: serverTimestamp(),
              };

              await updateEmployeePage(pageId, lastTransaction);

              const pageUrl = getAssignmentUrl(pageId);

              setUrlModalData({
                url: pageUrl,
                message: message,
              });
              setShowUrlModal(true);

              if (target.phone && !smsSentRef.current) {
                smsSentRef.current = true;
                sendAssignmentSms(
                  target.phone,
                  `${target.firstName} ${target.lastName}`,
                  pageUrl,
                  true,
                  false,
                ).catch((err) => console.warn("[SMS] send failed:", err));
              }
            } else {
              alert(message);
            }
          } catch (error) {
            console.error("Error updating employee page (transfer):", error);
            alert(message);
          }

          await refreshInventory();

          setShowTransferSignatureModal(false);
          setTransferTarget(null);
          setHasSignature(false);
          clearMainSignature();
          smsSentRef.current = false;
        } catch (error) {
          console.error("Error during signature transfer:", error);
          alert(
            `שגיאה בהעברה: ${error.message || "שגיאה לא ידועה"}. נא לנסות שוב.`,
          );
        }
      },
      [
        selectedEmployee,
        transferTarget,
        inventoryRowsForSignatureTransfer,
        hasSignature,
        signaturePadRef,
        getSignatureData,
        currentUser,
        personnel,
        clearMainSignature,
        setHasSignature,
        refreshInventory,
      ],
    );

    const handlePersonnelModalClose = useCallback(() => {
      setSelectedAssignmentId(null);
      closePersonnelConfirmModal();
    }, [closePersonnelConfirmModal, setSelectedAssignmentId]);

    const handlePersonnelConfirm = useCallback(
      async (personnelSignatureUrl) => {
        if (
          !personnelSignatureUrl ||
          typeof personnelSignatureUrl !== "string" ||
          !personnelSignatureUrl.startsWith("data:image")
        ) {
          alert("נא להוסיף חתימה לאישור ההקצאה");
          return;
        }

        const assignmentId = selectedAssignmentId;
        if (!assignmentId) {
          alert("לא נבחרה הקצאה");
          return;
        }

        try {
          if (!currentUser.email || currentUser.permissions !== "childUser") {
            alert("רק חיילים יכולים לאשר הקצאות");
            return;
          }

          const assignment = pendingAssignments.find(
            (a) => a.id === assignmentId,
          );
          if (!assignment) {
            alert("ההקצאה לא נמצאה");
            return;
          }

          const assignmentRef = doc(db, "assignments", assignmentId);
          await updateDoc(assignmentRef, {
            "assignments.second": {
              [assignment.personnelId]: assignment.assignments.first[
                assignment.personnelId
              ].map((item) => ({
                quantity: item.quantity,
                assignedAt: new Date(),
              })),
            },
            updatedAt: new Date(),
          });

          const batch = writeBatch(db);

          const currentPersonnel = personnel.find(
            (p) => p.email === currentUser.email,
          );

          if (assignment) {
            for (const item of assignment.items) {
              const inventoryItems = inventory.filter((invItem) => {
                if (invItem.itemDefinitionId !== item.itemId) return false;

                const firstAssignment =
                  invItem.assignments?.first?.[currentUser.employeeId];
                if (!firstAssignment || firstAssignment.quantity <= 0)
                  return false;

                if (item.serial) {
                  return invItem.serialNumber === item.serial;
                }

                return true;
              });

              for (const invItem of inventoryItems) {
                const inventoryRef = doc(db, "inventory", invItem.id);

                const currentDoc = await getDoc(inventoryRef);
                const currentData = currentDoc.data();

                const firstAssignment =
                  currentData?.assignments?.first?.[currentUser.employeeId] ||
                  invItem.assignments?.first?.[currentUser.employeeId];
                const quantity = firstAssignment?.quantity || 1;

                const employeePersonalNumber = assignment.personnelId;

                batch.update(inventoryRef, {
                  status: "assigned",
                  [`assignments.second.${employeePersonalNumber}`]: {
                    quantity: quantity,
                    assignedAt: serverTimestamp(),
                    firstName: currentPersonnel?.firstName || "Unknown",
                    lastName: currentPersonnel?.lastName || "User",
                  },
                  updatedAt: serverTimestamp(),
                });
              }
            }
          }

          await batch.commit();

          alert("הקצאה אושרה בהצלחה על ידי החייל!");

          await fetchPendingAssignments();
          closePersonnelConfirmModal();
          setSelectedAssignmentId(null);
          setPersonnelSignature("");
          setHasPersonnelSignature(false);
          clearPersonnelSignature();
        } catch (error) {
          console.error("Error confirming assignment:", error);
          alert("שגיאה באישור ההקצאה. נא לנסות שוב.");
        }
      },
      [
        selectedAssignmentId,
        pendingAssignments,
        inventory,
        personnel,
        currentUser,
        fetchPendingAssignments,
        closePersonnelConfirmModal,
        setSelectedAssignmentId,
        setPersonnelSignature,
        setHasPersonnelSignature,
        clearPersonnelSignature,
      ],
    );

    // Helper function to get remaining available quantity for an item
    const handleAssignedItemsFilterChange = useCallback((value) => {
      setAssignedItemsFilter(value);
    }, []);

    const handlePendingAssignmentStartConfirm = useCallback(
      (assignmentId) => {
        setSelectedAssignmentId(assignmentId);
        openPersonnelConfirmModal();
      },
      [openPersonnelConfirmModal, setSelectedAssignmentId],
    );

    const getRemainingQuantity = useCallback(
      (itemDefinitionId, isOrganic = null) => {
        const itemDefinition = items.find(
          (item) => item.id === itemDefinitionId,
        );
        if (!itemDefinition || itemDefinition.isChecked) return null;

        if (isChildUser && childUserPersonalNumber) {
          let totalAvailable = 0;
          inventory.forEach((invItem) => {
            if (
              invItem.itemDefinitionId === itemDefinitionId &&
              (isOrganic === null || invItem.isOrganic === isOrganic)
            ) {
              const firstAssignment =
                invItem.assignments?.first?.[childUserPersonalNumber];
              if (firstAssignment && firstAssignment.quantity > 0) {
                const firstQuantity = firstAssignment.quantity || 0;
                const secondAssignments = invItem.assignments?.second;
                let secondSum = 0;
                if (secondAssignments) {
                  secondSum = Object.values(secondAssignments).reduce(
                    (sum, assignment) => sum + (assignment.quantity || 0),
                    0,
                  );
                }
                totalAvailable += Math.max(0, firstQuantity - secondSum);
              }
            }
          });

          const currentCartQuantity = cart
            .filter((cartItem) => {
              return (
                cartItem.itemDefinitionId === itemDefinitionId &&
                (isOrganic === null || cartItem.isOrganic === isOrganic)
              );
            })
            .reduce(
              (total, cartItem) => total + Math.abs(cartItem.quantity),
              0,
            );

          return Math.max(0, totalAvailable - currentCartQuantity);
        }

        let totalAvailable = 0;
        inventory.forEach((invItem) => {
          if (
            invItem.itemDefinitionId === itemDefinitionId &&
            (isOrganic === null || invItem.isOrganic === isOrganic) &&
            !invItem.serialNumber
          ) {
            totalAvailable += invItem.availableQuantity ?? 0;
          }
        });

        const currentCartQuantity = cart
          .filter((cartItem) => {
            return (
              cartItem.itemDefinitionId === itemDefinitionId &&
              (isOrganic === null || cartItem.isOrganic === isOrganic)
            );
          })
          .reduce((total, cartItem) => total + Math.abs(cartItem.quantity), 0);

        return Math.max(0, totalAvailable - currentCartQuantity);
      },
      [cart, inventory, items, isChildUser, childUserPersonalNumber],
    );

    return (
      <div className="signature-content">
        {/* Personnel Search */}
        <PersonnelSearch
          personnel={availablePersonnel}
          onEmployeeSelect={handleEmployeeSelect}
          selectedEmployee={selectedEmployee}
          onClearSelection={handleClearSelection}
        />

        {/* Selected Employee Details */}
        {selectedEmployee && (
          <>
            <EmployeeDetailsCollapsible
              employee={selectedEmployee}
              getUnitName={getUnitName}
              formatBooleanValue={formatBooleanValue}
              collapsed={detailsCollapsed}
              onToggle={() => setDetailsCollapsed((v) => !v)}
            />
            {/* Inline item search; החלפת חתימות only in cart footer when עגלה has refund lines only */}
            <ItemSearchInline
              inventory={inventory}
              items={items}
              cart={cart}
              getRemainingQuantity={getRemainingQuantity}
              onAddItem={handleAddItem}
              isChildUser={isChildUser}
              childUserPersonalNumber={childUserPersonalNumber}
              onAddStockShortcut={goAddStockShortcut}
            />

            {/* Kit section */}
            <KitSection
              kits={kits}
              items={items}
              inventory={inventory}
              cart={cart}
              onAddItem={handleAddItem}
              getRemainingQuantity={getRemainingQuantity}
              isChildUser={isChildUser}
              childUserPersonalNumber={childUserPersonalNumber}
              onAddStockShortcut={goAddStockShortcut}
              canManageKits={!isChildUser}
              onSaveKit={async (name, kitItems, kitId) => {
                if (kitId) await updateKit(kitId, name, kitItems);
                else await addKit(name, kitItems, currentUser.id);
              }}
              onDeleteKit={deleteKit}
              onReorderKits={reorderKits}
            />

            {/* Cart Section */}
            <CartSection
              cart={cart}
              onRemoveFromCart={handleRemoveFromCart}
              onCartQuantityChange={handleCartQuantityChange}
              onCartNoteChange={updateCartNote}
              onClearCart={handleClearCart}
              onFinalizeAssignment={handleFinalizeAssignment}
              showTransferSignaturesInFooter={
                showTransferSignaturesInCartFooter
              }
              onTransferSignaturesClick={handleOpenTransferClick}
            />

            {/* Assigned Items Section */}
            {assignedItems && assignedItems.length > 0 && (
              <SignedAssignedItemsPanel
                filteredAssignedItems={filteredAssignedItems}
                assignedItemsFilter={assignedItemsFilter}
                onAssignedItemsFilterChange={handleAssignedItemsFilterChange}
                filterInputRef={filterInputRef}
                shouldMaintainFilterFocusRef={shouldMaintainFilterFocus}
                editingQuantities={editingQuantities}
                activeInputRef={activeInputRef}
                onReturnQuantityChange={handleReturnQuantityChange}
                onDebouncedEditQuantity={debouncedHandleEditQuantity}
                onAddReturnItem={handleAddReturnItem}
                onOpenAddItemModal={openAddItemModal}
              />
            )}
          </>
        )}

        {currentUser.email &&
          currentUser.permissions === "childUser" &&
          pendingAssignments.length > 0 && (
            <PendingAssignmentsList
              pendingAssignments={pendingAssignments}
              onStartConfirm={handlePendingAssignmentStartConfirm}
            />
          )}

        {/* Modals */}
        <AddItemModal
          isOpen={showAddItemModal}
          onClose={closeAddItemModal}
          inventory={inventory}
          items={items}
          onAddItem={handleAddItem}
          onRemoveFromCart={handleRemoveFromCart}
          onCartQuantityChange={handleCartQuantityChange}
          onClearCart={handleClearCart}
          cart={cart}
          getRemainingQuantity={getRemainingQuantity}
          isChildUser={isChildUser}
          childUserPersonalNumber={childUserPersonalNumber}
          availableItemNames={availableItemNames}
          selectedEmployee={selectedEmployee}
          onAddStockShortcut={
            user?.permissions === "admin" ||
            user?.permissions === "parentsUser"
              ? goAddStockShortcut
              : null
          }
        />

        {signatureStockFormPrefill != null && (
          <InventoryForm
            key={`sig-stock-${signatureStockFormPrefill.itemDefinitionId || "x"}-${signatureStockFormPrefill.serialNumber || ""}`}
            onSubmit={handleSignatureAddStockSubmit}
            onCancel={handleSignatureAddStockCancel}
            initialData={signatureStockFormPrefill}
          />
        )}

        <SignatureModal
          isOpen={showSignatureModal}
          onClose={closeSignatureModal}
          onConfirm={handleSignatureConfirm}
          selectedEmployee={selectedEmployee}
          cart={cart}
          signaturePadRef={signaturePadRef}
          setSignaturePadRef={setSignaturePadRef}
          hasSignature={hasSignature}
          setHasSignature={setHasSignature}
        />

        <SignatureTransferModal
          isOpen={showTransferPicker}
          onClose={handleTransferPickerClose}
          personnel={availablePersonnel}
          sourceEmployee={selectedEmployee}
          assignedCount={inventoryRowsForSignatureTransfer?.length || 0}
          onContinueToSignature={handleContinueTransferToSignature}
        />

        <SignatureModal
          variant="transfer"
          isOpen={showTransferSignatureModal}
          onClose={handleTransferSignatureModalClose}
          onConfirm={handleTransferSignatureConfirm}
          selectedEmployee={selectedEmployee}
          cart={[]}
          transferSourceEmployee={selectedEmployee}
          transferTargetEmployee={transferTarget}
          transferItems={transferSignatureModalItems}
          signaturePadRef={signaturePadRef}
          setSignaturePadRef={setSignaturePadRef}
          hasSignature={hasSignature}
          setHasSignature={setHasSignature}
        />

        <PersonnelConfirmModal
          key={selectedAssignmentId || "personnel-confirm"}
          isOpen={showPersonnelConfirmModal}
          onClose={handlePersonnelModalClose}
          onConfirm={handlePersonnelConfirm}
          assignment={pendingAssignments.find(
            (a) => a.id === selectedAssignmentId,
          )}
        />

        <UrlModal
          isOpen={showUrlModal}
          onClose={handleUrlModalClose}
          url={urlModalData.url}
          message={urlModalData.message}
        />
      </div>
    );
  },
);

export default SignatureTabContent;
